import { User } from 'firebase/auth';

export async function getAuthed<T>(path: string, authUser: User) {
  // don't inline due to TypeScript issue
  const idToken = await authUser.getIdToken();

  return get<T>(process.env.NEXT_PUBLIC_FUNCTIONS_BASE + path, {
    Authorization: `Bearer ${idToken}`,
  });
}

export async function get<T>(
  url: string,
  headers: { [p: string]: string } = {}
): Promise<T> {
  const response = await fetchWithTimeout(url, {
    method: 'get',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  await ensureSuccessful(response);
  return await bodyOf(response);
}

export async function postAuthed<T>(
  path: string,
  body: unknown,
  authUser: User
) {
  // don't inline due to TypeScript issue
  const idToken = await authUser.getIdToken();

  return await post<T>(process.env.NEXT_PUBLIC_FUNCTIONS_BASE + path, body, {
    Authorization: `Bearer ${idToken}`,
  });
}

export async function post<T>(
  url: string,
  body: unknown,
  headers: { [p: string]: string } = {}
): Promise<T> {
  const response = await fetchWithTimeout(url, {
    method: 'post',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  await ensureSuccessful(response);
  return await bodyOf(response);
}

async function bodyOf<T>(response: Response): Promise<T> {
  const responseBody = await response.text();

  try {
    return JSON.parse(responseBody) as T;
  } catch (e) {
    return { message: responseBody } as unknown as T;
  }
}

export async function postAuthDownload(
  path: string,
  body: unknown,
  fileName: string,
  authUser: User
) {
  const url = process.env.NEXT_PUBLIC_FUNCTIONS_BASE + path;
  // don't inline due to TypeScript issue
  const idToken = await authUser.getIdToken();

  const response = await fetchWithTimeout(url, {
    method: 'post',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  await ensureSuccessful(response);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob as Blob);

  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;

  const clickHandler = () =>
    setTimeout(() => URL.revokeObjectURL(objectUrl), 150);

  a.addEventListener('click', clickHandler, false);
  a.click();
}

async function fetchWithTimeout(url: RequestInfo, init?: RequestInit) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), 45_000);
  const response = await fetch(url, { ...init, signal: controller.signal });
  clearTimeout(id);
  return response;
}

async function ensureSuccessful(response: Response) {
  if (!response.ok) {
    const body = await bodyOf<{ userError?: boolean; error?: string }>(
      response
    );

    if (body.userError) {
      throw new UserError(body.error);
    } else {
      throw new HttpResponseError(response, JSON.stringify(body));
    }
  }
}

export class UserError extends Error {}

class HttpResponseError extends Error {
  constructor(response: Response, responseBody: string) {
    super(
      `${response.status} ${response.statusText} from ${response.url}\n` +
        responseBody
    );
  }
}
